<script setup lang="ts">
import '@fortawesome/fontawesome-free/css/all.min.css'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { normalizeFieldBool, normalizeFieldText } from '~/composables/useCustomFields'

const siteData = await useSiteData()
const route = useRoute()
const navigationEpoch = useNavigationEpoch()
const fields = siteData?.setting?.customFields

const themeMode = normalizeFieldText(fields, 'theme', 'system').toLowerCase()
const rawDarkColorScheme = normalizeFieldText(fields, 'darkColorScheme', 'yuelan').toLowerCase()
const darkColorScheme = rawDarkColorScheme === 'midnight' ? 'midnight' : 'yuelan'
const rawThemeColor = normalizeFieldText(fields, 'themeColor', '#1F6FEB').toUpperCase()
const themeColor = /^#[0-9A-F]{6}$/.test(rawThemeColor) ? rawThemeColor : '#1F6FEB'
const siteName = normalizeFieldText(fields, 'siteName', 'Yuelai Blog')
const siteDescription = normalizeFieldText(fields, 'siteDescription', normalizeFieldText(fields, 'seoDescription', ''))
const seoKeywords = normalizeFieldText(fields, 'seoKeywords', '')
const seoImage = normalizeFieldText(fields, 'seoImage', '')
const siteFavicon = normalizeFieldText(fields, 'siteFavicon', '')
const umamiEnabled = normalizeFieldBool(fields, 'umamiEnabled', false)
const umamiHost = normalizeFieldText(fields, 'umamiHost', '')
const umamiWebsiteId = normalizeFieldText(fields, 'umamiWebsiteId', '')
const customCss = normalizeFieldText(fields, 'customCss', '')
const customJs = normalizeFieldText(fields, 'customJs', '')
const isCustomThemeColor = themeColor !== '#1F6FEB'

const umamiScriptSrc = computed(() => {
  if (!umamiHost) {
    return ''
  }
  if (umamiHost.endsWith('.js')) {
    return umamiHost
  }
  return `${umamiHost.replace(/\/+$/, '')}/script.js`
})

const faviconHref = siteFavicon ? resolveImageAssetUrl(siteFavicon) : ''
const safeCustomJs = customJs.replace(/<\/script/gi, '<\\/script')

watch(
  () => `${route.path}?${JSON.stringify(route.query || {})}`,
  () => {
    navigationEpoch.value += 1
  },
  { immediate: true }
)

useSeoMeta({
  titleTemplate: titleChunk => (titleChunk ? `${titleChunk}` : siteName),
  description: siteDescription || undefined,
  keywords: seoKeywords || undefined,
  ogSiteName: siteName,
  ogDescription: siteDescription || undefined,
  ogImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined,
  twitterCard: seoImage ? 'summary_large_image' : undefined,
  twitterImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined
})

useHead(() => {
  const styles: Array<{
    key: string
    type: 'text/css'
    children: string
    tagPosition?: 'head' | 'bodyOpen' | 'bodyClose'
  }> = []
  const scripts: Array<
    | {
        defer: true
        src: string
        'data-website-id': string
      }
    | {
        key: string
        type: 'text/javascript'
        children: string
        tagPosition: 'bodyClose'
      }
  > = []
  if (umamiEnabled && umamiWebsiteId && umamiScriptSrc.value) {
    scripts.push({ defer: true, src: umamiScriptSrc.value, 'data-website-id': umamiWebsiteId })
  }
  if (safeCustomJs) {
    scripts.push({ key: 'theme-custom-js', type: 'text/javascript', children: safeCustomJs, tagPosition: 'bodyClose' })
  }
  if (isCustomThemeColor) {
    styles.push({
      key: 'theme-color',
      type: 'text/css',
      children: `:root{--theme-brand-light:${themeColor};--theme-brand-light-soft:color-mix(in srgb,${themeColor} 10%,#fff);--theme-brand-dark:color-mix(in srgb,${themeColor} 75%,#fff);--theme-brand-dark-soft:color-mix(in srgb,${themeColor} 20%,var(--bg-soft))}`
    })
  }
  if (customCss) {
    styles.push({
      key: 'theme-custom-css',
      type: 'text/css',
      children: customCss,
      tagPosition: 'bodyClose'
    })
  }

  return {
    htmlAttrs: {
      'data-dark-color-scheme': darkColorScheme
    },
    link: faviconHref
      ? [
          { rel: 'icon', href: faviconHref },
          { rel: 'shortcut icon', href: faviconHref }
        ]
      : [],
    style: styles,
    script: scripts
  }
})
</script>

<template>
  <BlogSvgSymbols />
  <NuxtLoadingIndicator :color="themeColor" :height="3" />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
